import React from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  HeartPulse,
  TrendingUp,
  Trophy,
  Building2,
  Briefcase,
  Phone,
  ClipboardList,
  CalendarCheck,
  Repeat,
  MapPin,
  ArrowRight,
} from 'lucide-react';
import { SectionHeading, FeatureCard, ImageBlock } from '@/components/Pieces';
import ContactForm from '@/components/ContactForm';
import FAQ from '@/components/FAQ';
import usePageMeta from '@/hooks/usePageMeta';
import { img } from '@/lib/site';

const OFFERINGS = [
  { icon: Users, title: 'Team Building Activities', desc: 'Interactive experiences designed to strengthen communication, collaboration, and team connection.' },
  { icon: HeartPulse, title: 'Wellness Experiences', desc: 'Activities focused on movement, recovery, stress management, and employee wellbeing.' },
  { icon: TrendingUp, title: 'Workforce Performance Programs', desc: 'Experiences that help employees improve energy, resilience, teamwork, and overall workplace performance.' },
  { icon: Trophy, title: 'Friendly Team Competitions', desc: 'Structured games and challenges that create engagement, excitement, and lasting memories.' },
];

const PARTNERS = [
  {
    icon: Building2,
    title: 'Retreat & Event Companies',
    desc: 'We partner with retreat planners, event agencies, and offsite providers looking to enhance their client experience with wellness, team building, and workforce performance programming.',
  },
  {
    icon: Briefcase,
    title: 'Organizations',
    desc: 'We support companies seeking innovative ways to improve employee engagement, morale, retention, and workplace culture.',
  },
];

const STEPS = [
  { icon: Phone, title: 'Discovery Call', desc: 'We learn about your goals, audience, and event needs.' },
  { icon: ClipboardList, title: 'Program Design', desc: 'We create a customized experience based on your objectives.' },
  { icon: CalendarCheck, title: 'Event Delivery', desc: 'Our team delivers the experience at your selected location.' },
  { icon: Repeat, title: 'Follow Up', desc: 'We provide recommendations and next steps to help maintain engagement beyond the event.' },
];

const VENUES = [
  'Corporate Offices',
  'Retreat Venues',
  'Resorts',
  'Conference Centers',
  'Parks',
  'Sports Complexes',
  'Approved Event Locations',
];

export default function RetreatEventsPage() {
  usePageMeta({
    title: 'Corporate Retreat, Team Building & Wellness Partner | Ball Raw Performance',
    description:
      'Ball Raw Performance partners with retreat and event companies to deliver team building activities, wellness experiences, and workforce performance programs that create more engaging and memorable employee experiences. Southern California corporate retreats and offsites.',
    path: '/retreat-events',
  });
  return (
    <div data-testid="page-retreat-events">
      {/* HERO */}
      <section className="relative bg-[#0A0A0A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={img('retreat_events')}
            alt="Corporate team retreat"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
          <p className="brp-overline brp-reveal">Retreat & Events</p>
          <h1 className="brp-reveal brp-delay-1 font-display uppercase font-bold text-5xl md:text-7xl lg:text-8xl leading-[0.92] mt-6 max-w-5xl">
            Turn Company Retreats Into <span className="text-[#D4AF37]">Meaningful Team Experiences.</span>
          </h1>
          <p className="brp-reveal brp-delay-2 mt-8 text-lg md:text-xl text-gray-300 max-w-3xl leading-relaxed">
            Ball Raw Performance partners with retreat and event companies to deliver team building activities, wellness experiences, and workforce performance programs that help organizations create more engaging and memorable employee experiences.
          </p>
          <div className="brp-reveal brp-delay-3 mt-10 flex flex-wrap gap-4">
            <a href="#contact" className="btn-brp-primary" data-testid="retreat-cta-discovery">
              Schedule A Discovery Call <ArrowRight size={16} />
            </a>
            <a href="#contact" className="btn-brp-outline-light" data-testid="retreat-cta-partner">
              Become A Partner
            </a>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#D4AF37]" />
      </section>

      {/* WHAT WE OFFER */}
      <section className="py-20 md:py-28 px-6 md:px-10 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeading
            overline="What We Offer"
            title="Experiences Designed To Move Teams."
            subtitle="Four program tracks that drop cleanly into any retreat, offsite, or corporate event."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {OFFERINGS.map((o, i) => (
              <FeatureCard
                key={o.title}
                icon={o.icon}
                title={o.title}
                desc={o.desc}
                accent
                testId={`offering-${i}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE WORK WITH */}
      <section className="py-20 md:py-28 px-6 md:px-10 bg-[#F9F9F9]">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeading
            overline="Who We Work With"
            title="Built For Planners & Built For Teams."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {PARTNERS.map((p, i) => (
              <div key={p.title} className="brp-card p-10 md:p-12" data-testid={`partner-${i}`}>
                <div className="w-14 h-14 flex items-center justify-center border border-[#D4AF37] mb-6">
                  <p.icon size={26} strokeWidth={1.5} className="text-[#D4AF37]" />
                </div>
                <h3 className="font-display uppercase tracking-wide text-2xl md:text-3xl font-bold">
                  {p.title}
                </h3>
                <p className="mt-4 text-[#555] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="py-20 md:py-28 px-6 md:px-10 bg-[#0A0A0A] text-white brp-noise relative">
        <div className="relative max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <SectionHeading
              light
              overline="Why It Matters"
              title="Strong Teams Are Built Through Shared Experiences."
              subtitle="Organizations that invest in employee engagement, workplace culture, and team connection often experience higher employee satisfaction, stronger collaboration, and improved retention. Ball Raw Performance helps create experiences that bring people together in a meaningful and memorable way."
            />
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-px bg-[#D4AF37]/30">
            {[
              { v: 'Higher', l: 'Employee Satisfaction' },
              { v: 'Stronger', l: 'Collaboration' },
              { v: 'Improved', l: 'Retention' },
              { v: 'Memorable', l: 'Team Connection' },
            ].map((s, i) => (
              <div key={s.l} className="bg-[#0A0A0A] p-6 md:p-8 border-l-4 border-[#D4AF37]" data-testid={`why-${i}`}>
                <div className="font-display text-3xl md:text-4xl font-bold text-[#D4AF37] leading-tight">
                  {s.v}
                </div>
                <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-gray-400 font-bold">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-28 px-6 md:px-10 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeading
            overline="How It Works"
            title="A Clear, Four-Step Process."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E5E5]">
            {STEPS.map((s, i) => (
              <div key={s.title} className="bg-white p-8 md:p-10 relative" data-testid={`step-${i}`}>
                <div className="font-display text-[#D4AF37] text-5xl font-bold leading-none">
                  0{i + 1}
                </div>
                <div className="w-12 h-12 flex items-center justify-center border border-[#0A0A0A] mt-6">
                  <s.icon size={20} strokeWidth={1.75} className="text-[#0A0A0A]" />
                </div>
                <h3 className="mt-5 font-display uppercase tracking-wide text-xl font-bold">{s.title}</h3>
                <p className="mt-3 text-[#555] leading-relaxed text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="py-20 md:py-28 px-6 md:px-10 bg-[#F9F9F9]">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <ImageBlock
              src={img('retreat_events')}
              alt="Service area"
              ratio="aspect-[4/3]"
              caption="Southern California"
            />
          </div>
          <div className="lg:col-span-6">
            <SectionHeading
              overline="Service Area"
              title="Programs Delivered Across Southern California."
              subtitle="Experiences can be hosted at corporate offices, retreat venues, resorts, conference centers, parks, sports complexes, and approved event locations."
            />
            <div className="mt-8 flex items-center gap-2 text-[#0A0A0A] font-display uppercase text-xs tracking-[0.2em]">
              <MapPin size={16} className="text-[#D4AF37]" />
              Available Hosting Environments
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {VENUES.map((v) => (
                <div key={v} className="flex items-start gap-3 border-l-2 border-[#D4AF37] pl-4 py-2" data-testid={`venue-${v.toLowerCase().replace(/\s+/g, '-')}`}>
                  <span className="font-display uppercase tracking-wide text-sm text-[#0A0A0A]">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 px-6 md:px-10 bg-[#0A0A0A] text-white">
        <div className="max-w-[1400px] mx-auto">
          <FAQ
            variant="dark"
            testIdPrefix="retreat-faq"
            overline="Frequently Asked"
            title="Frequently Asked Questions"
            items={[
              {
                q: 'How does a partnership with Ball Raw Performance work?',
                a: 'We collaborate with organizations and event partners to deliver customized team building, leadership, and workforce performance experiences. Every event is designed around your goals, audience, and schedule.',
              },
              {
                q: 'What experiences can you provide during a corporate retreat?',
                a: 'We offer team building activities, leadership sessions, recovery experiences, friendly competitions, and customized group activities. Every experience is designed to strengthen employee engagement and workplace relationships.',
              },
              {
                q: 'How do retreat experiences support company culture?',
                a: 'Retreats create opportunities for employees to connect, communicate, and collaborate outside of the workplace. These shared experiences help strengthen trust, morale, and organizational culture.',
              },
              {
                q: 'Can programs be customized for our organization?',
                a: 'Yes. Every retreat and event is tailored to your workforce, business objectives, and preferred event format.',
              },
              {
                q: 'Can Ball Raw Performance travel to our location?',
                a: 'Yes. We proudly serve organizations throughout Southern California and travel to approved client locations and event venues.',
              },
              {
                q: 'How far in advance should we begin planning?',
                a: 'We recommend beginning the planning process as early as possible to secure preferred dates and allow enough time for coordination.',
              },
            ]}
          />
        </div>
      </section>

      {/* FINAL CTA + CONTACT */}
      <section id="contact" className="py-20 md:py-28 px-6 md:px-10 bg-white scroll-mt-24">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="brp-overline">Let's Build It</p>
            <h2 className="mt-4 font-display uppercase tracking-tight font-bold text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
              Let's Create An Experience Your <span className="text-[#D4AF37]">Team Will Remember.</span>
            </h2>
            <p className="mt-6 text-[#555] leading-relaxed">
              Looking for a wellness, team building, or workforce performance partner for your next retreat or company event? Tell us about it.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#contact-form" className="btn-brp-primary" data-testid="retreat-final-cta-discovery">
                Schedule A Discovery Call
              </a>
              <a href="#contact-form" className="btn-brp-outline-dark" data-testid="retreat-final-cta-partner">
                Become A Retreat Partner
              </a>
            </div>
            <div className="mt-10 border-l-4 border-[#D4AF37] pl-5">
              <p className="text-[#555] italic">
                "Strong teams are built through shared experiences."
              </p>
            </div>
          </div>
          <div className="lg:col-span-7" id="contact-form">
            <ContactForm
              formType="retreat_event"
              variant="dark"
              title="Speak With Our Retreat & Events Team"
              subtitle="Tell us about your retreat, event, or partnership idea. We'll respond within one business day."
              showRole
              showEmployees
              showIndustry
              showInterest
              ctaLabel="Request Information"
              testIdPrefix="retreat-contact"
              sourcePage="/retreat-events"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
